import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileQuestion, Ban, Hourglass, RotateCcw, AlertTriangle } from 'lucide-react';
import { useRecordVisit } from '@/features/app-shell/hooks/useRecordVisit';
import { PageLayout } from '@/components/ui/page-layout';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import { Skeleton, PanelSkeleton } from '@/components/ui/skeleton';
import { TooltipProvider } from '@/components/ui/tooltip';
import { documentCaptureService } from '../services/documentCaptureService';
import { DocumentViewerContent } from '../components/viewer/DocumentViewerContent';
import { deriveViewerState, type ViewerState } from '../lib/viewerState';
import { apiErrorInfo } from '../lib/versionFormat';
import { useDocCaptureEvents } from '../hooks/useDocCaptureEvents';
import type { DocumentView } from '../types/documentCapture.types';

/**
 * Document viewer (86d3rrfdk) — the NetSuite landing target `/d/:docNo`. A logged-in user clicking
 * the DOP link inside a NetSuite transaction lands here. Login round-trips back to this exact URL
 * for free (ProtectedRoute stows `from`, LoginForm reads it).
 *
 * Edge states are DERIVED (there's no wire discriminator): withdrawn / processing from a 200,
 * and one neutral not-found for every 404 — which folds in missing, no-access AND not-yet-scanned,
 * because distinguishing them would leak whether a treasury document exists.
 */
export function DocumentViewerPage() {
  const { docNo } = useParams<{ docNo: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * Set when `/p/:token` sent us here after resolving a token that has since been REPLACED
   * (86d3uu104). Support's first move on "your link doesn't work" is to paste the customer's link, so
   * without this banner the document renders perfectly and they reply "works on my end" — the exact
   * failure the server's `superseded` flag exists to prevent.
   */
  const followedReplacedLink = (location.state as { supersededLink?: boolean } | null)?.supersededLink === true;
  const [doc, setDoc] = useState<DocumentView | null>(null);
  const [state, setState] = useState<ViewerState | 'loading'>('loading');
  const [reloadNonce, setReloadNonce] = useState(0);

  /* M4 (86d44z223) — Recent. The only kind whose id and label are the SAME value: this route is
     keyed by document NUMBER, so nothing extra needs resolving.
     Still gated on `doc` rather than on `docNo` alone — a mistyped or replaced URL must not add a
     Recent entry for a document the user never actually saw. */
  useRecordVisit('document', doc ? docNo : null, doc ? docNo : null);

  // Back affordance (user feedback 2026-07-27): rows navigate here in the same tab, so the viewer
  // needs an explicit way back. History-aware: an in-app arrival (stream/registry row) goes back to
  // the exact previous screen — filters and scroll intact; a cold arrival (the NetSuite deep-link,
  // a pasted URL) has no in-app history, so fall back to the Document Capture landing page.
  const goBack = useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate('/documents');
  }, [navigate]);

  /**
   * T7 — update/restore hand back the fresh view; re-derive so edge states stay honest.
   *
   * STABLE IDENTITY IS LOAD-BEARING (86d3w2jrj FE-3). This was an inline arrow. Once the viewer
   * started re-rendering on every stream event, a fresh arrow per render propagated down through
   * DocumentViewerContent into the update panel's verdict-poll dep array, so the 5-second interval
   * was torn down and recreated on each event — and during a decode, events for this document arrive
   * faster than the interval, so the poll would have stopped firing altogether. The panel would sit
   * on "checking" forever while the verdict was already recorded.
   */
  const applyFreshDoc = useCallback((d: DocumentView) => {
    setDoc(d);
    setState(deriveViewerState(d));
  }, []);

  /**
   * `silent` re-reads the document WITHOUT collapsing to the skeleton or clearing what is shown: the
   * new payload swaps in when it lands, and a failure leaves the working view alone. That is the
   * only shape a background refresh may take — flashing a skeleton (or worse, a "not found" card)
   * under someone reading a PDF because a stream event arrived would be a downgrade on the staleness
   * it is meant to fix.
   */
  const loadDoc = useCallback(
    async (silent: boolean) => {
      if (!docNo) {
        setState('notfound');
        return;
      }
      if (!silent) setState('loading');
      try {
        const d = await documentCaptureService.getDocument(docNo);
        setDoc(d);
        setState(deriveViewerState(d));
      } catch (err) {
        if (silent) return;
        setDoc(null);
        // A 404 is the deliberate uniform neutral answer (missing / no-access / not-yet-scanned) —
        // never leak more than "not available". A transport/5xx error is NOT document-specific and
        // leaks nothing, so offer a retry instead of a permanent dead-end (86d3uf18j L7).
        setState(apiErrorInfo(err).status === 404 ? 'notfound' : 'error');
      }
    },
    [docNo]
  );

  useEffect(() => {
    void loadDoc(false);
  }, [loadDoc, reloadNonce]);

  /**
   * 86d3w2jrj FE-3 — the viewer now follows its own document live. Before this, everything the
   * pipeline did to a document you were looking at (a decode landing, a colleague approving a
   * replacement, the NetSuite link attaching) was invisible until a manual refresh, even though the
   * event describing it was already being broadcast to the capture page.
   *
   * Filtered to this docNumber by the hook, and deliberately a REFETCH rather than a patch: the
   * event carries a phase, not a document, and the payload here drives version history, link status
   * and the edge-state derivation. Re-reading is a handful of milliseconds and cannot drift.
   */
  useDocCaptureEvents({
    docNumber: docNo ?? null,
    enabled: !!docNo,
    debounceMs: 250,
    onEvent: () => void loadDoc(true),
  });

  return (
    <TooltipProvider>
      <PageLayout
        header={
          <div className="space-y-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={goBack}
              className="-ml-2 gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Document Capture
            </Button>
            <PageHeader title={docNo ?? 'Document'} mono />
          </div>
        }
      >
        {state === 'loading' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
            {/* Skeleton mirrors the canvas + metadata layout, so the swap-in doesn't jump. */}
            <Skeleton className="h-[70vh] w-full rounded-xl" />
            <div className="space-y-4">
              <PanelSkeleton rows={4} />
              <PanelSkeleton rows={3} />
            </div>
          </div>
        )}

        {state === 'notfound' && (
          <EmptyState
            icon={FileQuestion}
            title="Document not found"
            description="This document doesn’t exist, hasn’t been scanned yet, or isn’t available to you."
          />
        )}

        {state === 'error' && (
          <EmptyState
            icon={RotateCcw}
            title="Couldn’t load this document"
            description="Something went wrong reaching the server. This isn’t about the document itself — try again in a moment."
            action={
              <Button variant="outline" size="sm" onClick={() => setReloadNonce((n) => n + 1)}>
                Try again
              </Button>
            }
          />
        )}

        {state === 'withdrawn' && (
          <EmptyState
            icon={Ban}
            title="This document was withdrawn"
            description="The scan was rejected and its NetSuite link removed. It’s kept for the audit trail but is no longer served."
          />
        )}

        {state === 'processing' && (
          <EmptyState
            icon={Hourglass}
            title="Still processing"
            description="This scan has arrived and is being read. It’ll appear here in a moment — no action needed."
          />
        )}

        {followedReplacedLink && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-warning/25 bg-warning/10 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
            <p className="text-xs leading-relaxed text-foreground">
              <span className="font-medium">You followed a link that has been replaced.</span> You can
              see this document because you have permission — but the customer holding that link
              cannot. Send them the current one from <span className="font-medium">Manage link</span>.
            </p>
          </div>
        )}

        {state === 'ready' && doc && <DocumentViewerContent doc={doc} onDocUpdated={applyFreshDoc} />}
      </PageLayout>
    </TooltipProvider>
  );
}
